import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import LeaveConfirmModal from '../ui/LeaveConfirmModal';

const NavigationGuardContext = createContext(null);

export const NavigationGuardProvider = ({ children }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const pendingNavRef = useRef(null);
  const processingRef = useRef(false);
  const startingPathRef = useRef(window.location.pathname);
  const bypassNextRef = useRef(false);

  const isDifferentPath = (url) => {
    if (!url || typeof url !== 'string') return false;
    const current = startingPathRef.current.replace(/\/+$/, '') || '/';
    const target = url.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
    return target !== current;
  };

  // Intercept history.pushState and replaceState
  useEffect(() => {
    const originalPushState = window.history.pushState.bind(window.history);
    const originalReplaceState = window.history.replaceState.bind(window.history);

    const pushStateInterceptor = (...args) => {
      if (bypassNextRef.current) {
        bypassNextRef.current = false;
        originalPushState(...args);
        return;
      }
      if (processingRef.current && isDifferentPath(args[2])) {
        pendingNavRef.current = () => originalPushState(...args);
        setShowConfirm(true);
        return;
      }
      originalPushState(...args);
    };

    const replaceStateInterceptor = (...args) => {
      if (bypassNextRef.current) {
        bypassNextRef.current = false;
        originalReplaceState(...args);
        return;
      }
      if (processingRef.current && isDifferentPath(args[2])) {
        pendingNavRef.current = () => originalReplaceState(...args);
        setShowConfirm(true);
        return;
      }
      originalReplaceState(...args);
    };

    window.history.pushState = pushStateInterceptor;
    window.history.replaceState = replaceStateInterceptor;

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);

  // Intercept popstate (browser back/forward)
  useEffect(() => {
    const handlePopState = () => {
      if (processingRef.current) {
        const targetPath = window.location.pathname;
        window.history.pushState(null, '', startingPathRef.current);

        pendingNavRef.current = () => {
          window.history.pushState(null, '', targetPath);
        };
        setShowConfirm(true);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const setProcessing = useCallback((val) => {
    processingRef.current = val;
    if (val) {
      startingPathRef.current = window.location.pathname;
    }
    if (!val) {
      setShowConfirm(false);
      pendingNavRef.current = null;
    }
  }, []);

  const bypassOnce = useCallback(() => {
    bypassNextRef.current = true;
  }, []);

  const confirmNavigation = useCallback(() => {
    processingRef.current = false;
    setShowConfirm(false);
    if (pendingNavRef.current) {
      const nav = pendingNavRef.current;
      pendingNavRef.current = null;
      nav();
    }
  }, []);

  const cancelNavigation = useCallback(() => {
    setShowConfirm(false);
    pendingNavRef.current = null;
  }, []);

  return (
    <NavigationGuardContext.Provider
      value={{ showConfirm, setProcessing, bypassOnce, confirmNavigation, cancelNavigation }}
    >
      {children}
      <LeaveConfirmModal
        isOpen={showConfirm}
        onStay={cancelNavigation}
        onLeave={confirmNavigation}
      />
    </NavigationGuardContext.Provider>
  );
};

export const useNavigationGuard = () => {
  const ctx = useContext(NavigationGuardContext);
  if (!ctx) throw new Error('useNavigationGuard must be used within NavigationGuardProvider');
  return ctx;
};

export default NavigationGuardContext;
